import { Inject } from '@odinjs/odin';

import { ButtonAPI, PanelAPI, TextAPI, TextAreaAPI, TextFontSize, VirtualAssistantButtonAPI, VirtualAssistantButtonStatus } from '@philips-internal/oswald-ui-components/lib/components';
import Controller, { OnClick, OnClose, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { TimerAPI } from '@philips-emr/health-professional-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';

import { DialogType, ModalType, SimpleDialogParams } from '../../../interfaces/simple-dialog';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { ConsentTranscriptAliases, FragmentNames } from '../../../codegen/index';
import { OpenConsentModalService } from './open-consent-modal-service';
import { ApplicationRoute } from '../../../constants/routes';
import ShowToast from '../../../utils/toast-service';
import { SimpleDialog } from '../../dialog-helper';
import BaseController from '../../BaseController';
import { RecordingEvent, RecordingState } from './smartcare/smartcare-transcription';
import { SmartCareTranscriptionFactory } from './smartcare/smartcare-transcription-factory';
import SmartcareMetricxTags from './smartcare/smartcare-metricx-tags';

import './recording-of-consent.css'

@Controller({ component: ConsentTranscriptAliases.LAYOUT_CONSENT_TRANSCRIPT, fragment: FragmentNames.CONSENT_TRANSCRIPT })
export default class ConsentTranscriptController extends BaseController {

  @Inject(ConsentTranscriptAliases.BUTTON_SAVE_TRANSCRIPT)
  public buttonSaveTranscriptApi!: ButtonAPI;

  @Inject(ConsentTranscriptAliases.PANEL_GLOBAL_ACTION_CONSENT_TRANSCRIPT)
  private panelGlobalActionConsentTranscriptApi!: PanelAPI;

  @Inject(ConsentTranscriptAliases.TEXT_STATUS)
  private textStatusApi!: TextAPI;

  @Inject(ConsentTranscriptAliases.TEXT_TITLE)
  private textTitleApi!: TextAPI;

  @Inject(ConsentTranscriptAliases.TEXT_SUBTITLE)
  private textSubtitleApi!: TextAPI;

  @Inject(ConsentTranscriptAliases.BUTTON_SHOW_TRANSCRIPT_TEXTS)
  private buttonShowTranscriptTextApi!: ButtonAPI;

  @Inject(ConsentTranscriptAliases.PANEL_TRANSCRIPT_TEXTS)
  private panelTranscriptTextsApi!: PanelAPI;

  @Inject(ConsentTranscriptAliases.TIMER)
  private timerApi!: TimerAPI;

  @Inject(ConsentTranscriptAliases.VA_BUTTON)
  declare virtualAssistantButtonApi: VirtualAssistantButtonAPI;

  private url = this.historyService.getCurrentRouteState().smartCareUrl as string
  private readonly smartcareService = SmartCareTranscriptionFactory.createTranscriptionService(this.url, SmartcareMetricxTags.mountTags());

  @OnMount(ConsentTranscriptAliases.LAYOUT_CONSENT_TRANSCRIPT)
  handleMount(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId, patientId });
    this.smartcareService.cleanTranscription()
  }

  @OnMount(ConsentTranscriptAliases.BUTTON_SAVE_TRANSCRIPT)
  handleMountSaveTranscript({ target }: EventData<ButtonAPI>): void {
    target.setEnabled(!this.smartcareService.transcriptionIsEmpty())
  }

  @OnMount(ConsentTranscriptAliases.LAYOUT_CONTENT_RECORDING)
  handleMountRecordTranscript(): void {
    const smartCareAudioRecord = this.smartcareService.getAudioRecordInstance();
    if (smartCareAudioRecord.state !== RecordingState.INACTIVE) {
      return
    }

    smartCareAudioRecord.clearEventListeners()

    smartCareAudioRecord.addEventListener(RecordingEvent.START, () => {
      this.buttonSaveTranscriptApi.setEnabled(false)
      this.panelGlobalActionConsentTranscriptApi.setVisible(false);
    });

    smartCareAudioRecord.addEventListener(RecordingEvent.RESUME, () => {
      this.buttonSaveTranscriptApi.setEnabled(false)
      this.panelGlobalActionConsentTranscriptApi.setVisible(false);
    });
  }

  @OnMount(ConsentTranscriptAliases.TEXT_AREA_TRANSCRIPT_RESULTS)
  handleMountDeck0({ target }: EventData<TextAreaAPI>): void {
    target.setValue(this.smartcareService.getTranscriptionData());
  }

  @OnClick(ConsentTranscriptAliases.BUTTON_SAVE_TRANSCRIPT)
  handleSaveTranscript(): void {
    this.smartcareService.pause();
    this.virtualAssistantButtonApi.setMediaStream(undefined);

    if (this.smartcareService.transcriptionIsEmpty()) {
      ShowToast.open(MessageService.translate('text.transcription-is-necessary)'), ToastLevel.WARNING);
      return
    }

    this.smartcareService.stop();
    const transcription = this.smartcareService.getTranscriptionData();
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const body = {
      encounterId: Number(encounterId),
      evolutionDescription: transcription,
      patientId: Number(patientId),
    }

    OpenConsentModalService.saveSmartCareClinicalNote(body).then((response) => {
      if (response && response.evolutionId > 0) {
        ShowToast.open(MessageService.translate('text.successfully-generated-clinical-notes'), ToastLevel.SUCCESS);
        applicationContext.redirect(ApplicationRoute.SUEP_CLINICAL_NOTES_LIST, { encounterId, patientId });
        return Promise.resolve();
      }
      ShowToast.open(MessageService.translate('text.no-clinical-note-registered-for-transcription'), ToastLevel.WARNING)
      return Promise.resolve();
    });
  }

  @OnClick({ target: ConsentTranscriptAliases.BUTTON_DISCARD_TRANSCRIPT })
  handleConsentTranscriptCancel(): void {
    this.smartcareService.pause();
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: 'clinical-notes.discard-changes',
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => {
        applicationContext.popNavigationItem();
      },
      onClickOk: () => {
        this.virtualAssistantButtonApi.setMediaStream(undefined);
        this.smartcareService.stop();
        applicationContext.redirect(ApplicationRoute.SUEP_CLINICAL_NOTES_LIST, { encounterId, patientId });
      },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  @OnClick(ConsentTranscriptAliases.VA_BUTTON)
  async handleRecordClick(): Promise<void> {
    if (this.virtualAssistantButtonApi.getStatus() === VirtualAssistantButtonStatus.PROCESSING) {
      return
    }

    switch (this.smartcareService.getState()) {
      case RecordingState.RECORDING:
        this.pauseRecording();
        break;
      case RecordingState.PAUSED:
        this.resumeRecording();
        break;
      default:
        this.startRecording();
        break;
    }
  }

  @OnClick(ConsentTranscriptAliases.BUTTON_SHOW_TRANSCRIPT_TEXTS)
  handleClickShowTrancriptText(): void {
    this.panelTranscriptTextsApi.setVisible(true);
  }

  @OnClick(ConsentTranscriptAliases.BUTTON_CLOSE_TRANSCRIPT_TEXTS)
  handleClickCloseTrancriptText(): void {
    this.panelTranscriptTextsApi.setVisible(false);
  }

  @OnClose(ConsentTranscriptAliases.MODAL_TRANSCRIPT_TEXTS)
  handleCloseModalTranscriptTexts(): void {
    this.panelTranscriptTextsApi.setVisible(false);
  }

  private async startRecording(): Promise<void> {
    this.smartcareService.start()
      .then(() => {
        this.virtualAssistantButtonApi.setMediaStream(this.smartcareService.getStream());
        this.textTitleApi.setSize(TextFontSize.DEFAULT)
        this.textSubtitleApi.setValue('')
        this.processingTranscription()
      });
  }

  public async pauseRecording(): Promise<void> {
    this.virtualAssistantButtonApi.setMediaStream(undefined);
    this.timerApi.stopTimer();
    this.virtualAssistantButtonApi.setStatus(VirtualAssistantButtonStatus.PROCESSING);
    this.textStatusApi.setValue('Por favor, aguarde um momento')
    this.textTitleApi.setValue(MessageService.translate('text.pause'))
    this.buttonShowTranscriptTextApi.setVisible(true)
    this.buttonShowTranscriptTextApi.setLoading(true)
    this.smartcareService.pause()
      .finally(() => {
        this.virtualAssistantButtonApi.setStatus(VirtualAssistantButtonStatus.DEFAULT);
        this.textStatusApi.setValue(MessageService.translate('text.touch-to-return'))
        this.buttonShowTranscriptTextApi.setLoading(false)
        this.panelGlobalActionConsentTranscriptApi.setVisible(true);
      });
  }

  private processingTranscription(): void {
    this.timerApi.startTimer();
    this.virtualAssistantButtonApi.setStatus(VirtualAssistantButtonStatus.LISTENING);
    this.textTitleApi.setValue(MessageService.translate('text.listening'))
    this.textStatusApi.setValue(MessageService.translate('text.touch-to-pause'))
    this.buttonShowTranscriptTextApi.setVisible(false)
  }

  private async resumeRecording(): Promise<void> {
    this.virtualAssistantButtonApi.setMediaStream(this.smartcareService.getStream());
    this.smartcareService.resume()
    this.processingTranscription()
  }

  @OnUnmount(ConsentTranscriptAliases.LAYOUT_CONSENT_TRANSCRIPT)
  handleUnmount(): void {
    this.smartcareService.cleanTranscription();
    this.smartcareService.stop();
  }
}